import React, { useMemo, useState } from 'react';
import { Link } from 'react-router-dom';
import { useTranslation } from 'react-i18next';
import cn from 'classnames';

import { ReactComponent as ExpandIcon } from '../../components/TagsHierarchyChooser/assets/chevron.svg';
import { LoaderSmall } from '../../components/LoaderSmall/LoaderSmall';
import { artifactsApi, ArtifactBundleListItemDTO, ArtifactDTO } from '../../utils/artifactsApi';
import { ARTIFACT_TYPE_LABELS, formatPublishedAt } from '../../utils/artifactsUi';
import { showRequestError } from '../../utils/requestError';
import styles from './ArtifactRegistryPage.module.scss';

interface ArtifactRegistryTreeProps {
  bundles: ArtifactBundleListItemDTO[];
  myWorkspaceId: number | undefined;
}

function bundleLink(bundle: ArtifactBundleListItemDTO, myWorkspaceId: number | undefined, itemId?: string): string {
  const params = new URLSearchParams();
  if (bundle.workspace_id !== myWorkspaceId) params.set('ws', String(bundle.workspace_id));
  if (itemId) params.set('item', itemId);
  const query = params.toString();
  return `/artifacts/${bundle.id}${query ? `?${query}` : ''}`;
}

// Workspace → bundle → artifact hierarchy over the current result page. Bundle
// children are fetched on first expand (the list carries counts, not members).
export function ArtifactRegistryTree({ bundles, myWorkspaceId }: ArtifactRegistryTreeProps) {
  const { t, i18n } = useTranslation();
  const [collapsedWorkspaces, setCollapsedWorkspaces] = useState<Set<number>>(new Set());
  const [expandedBundles, setExpandedBundles] = useState<Set<string>>(new Set());
  const [members, setMembers] = useState<Record<string, ArtifactDTO[] | null>>({});

  const byWorkspace = useMemo(() => {
    const groups = new Map<number, ArtifactBundleListItemDTO[]>();
    bundles.forEach((bundle) => {
      const group = groups.get(bundle.workspace_id) ?? [];
      group.push(bundle);
      groups.set(bundle.workspace_id, group);
    });
    return Array.from(groups.entries()).sort(([a], [b]) => a - b);
  }, [bundles]);

  const toggleWorkspace = (workspaceId: number) =>
    setCollapsedWorkspaces((prev) => {
      const next = new Set(prev);
      next.has(workspaceId) ? next.delete(workspaceId) : next.add(workspaceId);
      return next;
    });

  const toggleBundle = (bundle: ArtifactBundleListItemDTO) => {
    setExpandedBundles((prev) => {
      const next = new Set(prev);
      next.has(bundle.id) ? next.delete(bundle.id) : next.add(bundle.id);
      return next;
    });
    if (members[bundle.id] !== undefined) return;
    setMembers((prev) => ({ ...prev, [bundle.id]: null }));
    artifactsApi
      .getBundle(bundle.id, { workspaceId: bundle.workspace_id !== myWorkspaceId ? bundle.workspace_id : undefined })
      .then((detail) =>
        setMembers((prev) => ({
          ...prev,
          [bundle.id]: [...detail.artifacts].sort((a, b) => a.position - b.position),
        })),
      )
      .catch((error) => {
        // drop the pending marker so the next expand retries instead of showing an empty bundle
        setMembers((prev) => {
          const next = { ...prev };
          delete next[bundle.id];
          return next;
        });
        setExpandedBundles((prev) => {
          const next = new Set(prev);
          next.delete(bundle.id);
          return next;
        });
        showRequestError(error, t('Artifacts'));
      });
  };

  return (
    <div className={styles.tree}>
      {byWorkspace.map(([workspaceId, workspaceBundles]) => {
        const collapsed = collapsedWorkspaces.has(workspaceId);
        return (
          <div key={workspaceId} className={styles.treeWorkspace}>
            <button type="button" className={styles.treeRow} onClick={() => toggleWorkspace(workspaceId)}>
              <ExpandIcon className={cn(styles.treeChevron, { [styles.treeChevronOpen]: !collapsed })} aria-hidden="true" />
              <span className={styles.treeWorkspaceName}>
                {t('Workspace')} #{workspaceId}
                {workspaceId === myWorkspaceId && <span className={styles.treeMine}>{t('My workspace')}</span>}
              </span>
              <span className={styles.treeCount}>{workspaceBundles.length}</span>
            </button>
            {!collapsed &&
              workspaceBundles.map((bundle) => {
                const expanded = expandedBundles.has(bundle.id);
                const bundleMembers = members[bundle.id];
                return (
                  <div key={bundle.id} className={styles.treeBundle}>
                    <div className={styles.treeRow}>
                      <button
                        type="button"
                        className={styles.treeToggle}
                        onClick={() => toggleBundle(bundle)}
                        aria-label={expanded ? t('Collapse') : t('Expand')}
                      >
                        <ExpandIcon className={cn(styles.treeChevron, { [styles.treeChevronOpen]: expanded })} aria-hidden="true" />
                      </button>
                      <Link to={bundleLink(bundle, myWorkspaceId)} className={styles.treeBundleTitle}>
                        {bundle.title}
                      </Link>
                      {bundle.status !== 'PUBLISHED' && (
                        <span className={styles.treeStatus}>{t(bundle.status === 'DRAFT' ? 'Draft' : 'Archived')}</span>
                      )}
                      <span className={styles.treeMeta}>
                        {bundle.artifact_count} · {formatPublishedAt(bundle.published_at, i18n.language)}
                      </span>
                    </div>
                    {expanded && bundleMembers === null && (
                      <div className={styles.treeArtifacts}>
                        <LoaderSmall />
                      </div>
                    )}
                    {expanded && bundleMembers && (
                      <div className={styles.treeArtifacts}>
                        {bundleMembers.map((artifact) => (
                          <Link
                            key={artifact.id}
                            to={bundleLink(bundle, myWorkspaceId, artifact.id)}
                            className={styles.treeArtifact}
                          >
                            <span className={styles.treeType}>{t(ARTIFACT_TYPE_LABELS[artifact.type])}</span>
                            {artifact.title}
                          </Link>
                        ))}
                      </div>
                    )}
                  </div>
                );
              })}
          </div>
        );
      })}
    </div>
  );
}
